/**
 * `offboarding` endpoint wrappers.
 *
 * Field shapes are derived from the offboarding serializers — see
 * src/types/offboarding.ts for the full derivation notes. Route paths and
 * ordering are verified against refactor/backend/src/modules/offboarding/routes.ts.
 *
 * `initiate` is the real workflow entry point (auto-runs reconciliation
 * server-side), NOT the plain `POST requests/` CRUD create — see the route
 * file's top comment. Both are wrapped here, but pages should build their
 * primary "start offboarding" action around `initiateOffboarding`.
 */
import { apiClient } from '../client';
import { OFFBOARDING_INSPECTIONS, OFFBOARDING_REQUESTS, OFFBOARDING_SETTLEMENTS } from './paths';
import type { Paginated } from '../types';
import type {
  DepositSettlement,
  DepositSettlementCreateRequest,
  DepositSettlementPatchRequest,
  InitiateOffboardingRequest,
  InspectionItem,
  InspectionItemCreateRequest,
  InspectionItemPatchRequest,
  OffboardingRequest,
  OffboardingRequestCreateRequest,
  OffboardingRequestPatchRequest,
  OffboardingRequestRow,
} from '../../types/offboarding';

// ---------------------------------------------------------------------------
// requests/
// ---------------------------------------------------------------------------

/** `GET offboarding/requests/` — paginated, scoped to the caller. */
export async function listOffboardingRequests(params?: {
  page?: number | undefined;
}): Promise<Paginated<OffboardingRequestRow>> {
  const response = await apiClient.get<Paginated<OffboardingRequestRow>>(OFFBOARDING_REQUESTS, {
    params,
  });
  return response.data;
}

/** `GET offboarding/requests/{id}/`. */
export async function getOffboardingRequest(id: number): Promise<OffboardingRequest> {
  const response = await apiClient.get<OffboardingRequest>(`${OFFBOARDING_REQUESTS}${id}/`);
  return response.data;
}

/**
 * `POST offboarding/requests/initiate/` — manager roles only. Body is
 * `{lease_id}`, not the generic create schema; also re-runs reconciliation
 * server-side in the same transaction.
 */
export async function initiateOffboarding(
  payload: InitiateOffboardingRequest,
): Promise<OffboardingRequest> {
  const response = await apiClient.post<OffboardingRequest>(
    `${OFFBOARDING_REQUESTS}initiate/`,
    payload,
  );
  return response.data;
}

/** `POST offboarding/requests/{id}/calculate_reconciliation/` — manager roles only, no body. */
export async function calculateReconciliation(id: number): Promise<OffboardingRequest> {
  const response = await apiClient.post<OffboardingRequest>(
    `${OFFBOARDING_REQUESTS}${id}/calculate_reconciliation/`,
  );
  return response.data;
}

/**
 * `POST offboarding/requests/{id}/complete/` — manager roles only, no body.
 * Also terminates the lease server-side.
 */
export async function completeOffboarding(id: number): Promise<OffboardingRequest> {
  const response = await apiClient.post<OffboardingRequest>(`${OFFBOARDING_REQUESTS}${id}/complete/`);
  return response.data;
}

/** `POST offboarding/requests/` — plain CRUD create; manager roles only. */
export async function createOffboardingRequest(
  payload: OffboardingRequestCreateRequest,
): Promise<OffboardingRequest> {
  const response = await apiClient.post<OffboardingRequest>(OFFBOARDING_REQUESTS, payload);
  return response.data;
}

/** `PATCH offboarding/requests/{id}/` — manager roles only. */
export async function updateOffboardingRequest(
  id: number,
  payload: OffboardingRequestPatchRequest,
): Promise<OffboardingRequest> {
  const response = await apiClient.patch<OffboardingRequest>(`${OFFBOARDING_REQUESTS}${id}/`, payload);
  return response.data;
}

/** `DELETE offboarding/requests/{id}/` — manager roles only. */
export async function deleteOffboardingRequest(id: number): Promise<void> {
  await apiClient.delete(`${OFFBOARDING_REQUESTS}${id}/`);
}

// ---------------------------------------------------------------------------
// inspections/
// ---------------------------------------------------------------------------

/** `GET offboarding/inspections/` — paginated, scoped to the caller. */
export async function listInspectionItems(params?: {
  page?: number | undefined;
}): Promise<Paginated<InspectionItem>> {
  const response = await apiClient.get<Paginated<InspectionItem>>(OFFBOARDING_INSPECTIONS, {
    params,
  });
  return response.data;
}

/**
 * `POST offboarding/inspections/` — manager roles only. Adding an item
 * re-runs reconciliation server-side automatically.
 */
export async function createInspectionItem(
  payload: InspectionItemCreateRequest,
): Promise<InspectionItem> {
  const response = await apiClient.post<InspectionItem>(OFFBOARDING_INSPECTIONS, payload);
  return response.data;
}

/**
 * `PATCH offboarding/inspections/{id}/` — manager roles only. Does NOT
 * re-run reconciliation (documented Django gap, ported as-is).
 */
export async function updateInspectionItem(
  id: number,
  payload: InspectionItemPatchRequest,
): Promise<InspectionItem> {
  const response = await apiClient.patch<InspectionItem>(`${OFFBOARDING_INSPECTIONS}${id}/`, payload);
  return response.data;
}

/** `DELETE offboarding/inspections/{id}/` — manager roles only. */
export async function deleteInspectionItem(id: number): Promise<void> {
  await apiClient.delete(`${OFFBOARDING_INSPECTIONS}${id}/`);
}

// ---------------------------------------------------------------------------
// settlements/
// ---------------------------------------------------------------------------

/** `POST offboarding/settlements/` — manager roles only; `offboarding` is unique (one settlement per offboarding). */
export async function createDepositSettlement(
  payload: DepositSettlementCreateRequest,
): Promise<DepositSettlement> {
  const response = await apiClient.post<DepositSettlement>(OFFBOARDING_SETTLEMENTS, payload);
  return response.data;
}

/** `PATCH offboarding/settlements/{id}/` — manager roles only. */
export async function updateDepositSettlement(
  id: number,
  payload: DepositSettlementPatchRequest,
): Promise<DepositSettlement> {
  const response = await apiClient.patch<DepositSettlement>(`${OFFBOARDING_SETTLEMENTS}${id}/`, payload);
  return response.data;
}
